import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import Badge from 'components/field/Badge';
import Alert from 'components/global/Alert';
import Icon from 'components/global/Icon';
import Image from 'components/global/Image';
import Skeleton from 'components/global/Skeleton';
import Tooltip from 'components/global/Tooltip';
import Typography from 'components/global/Typography';
import { FilterAction, FilterType } from 'components/list/DynamicListFilterUtils';

import useFetchAndParseOnView from 'hooks/useFetchAndParseOnView';

import { REPORT } from 'services/consts';

import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';

import { SectionWrapper } from '../OnboardingV2/Onboarding.styled';
import * as S from './ShipmentsNeedAction.styled';

const shipmentsNeedActionConfig = [
	{
		imageUrl: 'https://storage.googleapis.com/tryoto-public/public/assets/icons/notifications/warning.svg',
		header: 'shipmentsOnHold',
		description: 'shipmentsOnHoldDescription',
		navigationLink: '/shipments/onhold-orders'
	},
	{
		imageUrl: 'https://storage.googleapis.com/tryoto-public/public/assets/images/homepage/delayedShipments.svg',
		header: 'delayedShipments',
		description: 'delayedShipmentsDescription',
		navigationLink: '/shipments/awaiting-pickup'
	}
];

const ShipmentsNeedAction = () => {
	const { t } = useTranslation();
	const [numberOfShipmentsOnHold, setNumberOfShipmentsOnHold] = useState(0);
	const [numberOfDelayedShipments, setnumberOfDelayedShipments] = useState(0);
	const [delayedShipments, setDelayedShipments] = useState([]);
	const body = useMemo(() => ({}), []);
	const navigate = useNavigate();

	const {
		ref,
		data: dashboardData,
		loading
	} = useFetchAndParseOnView({
		url: REPORT.DASHBOARD.GET_DASHBOARD_SHIPMENT_NEED_ACTION,
		body
	});

	useEffect(() => {
		if (dashboardData) {
			setNumberOfShipmentsOnHold(dashboardData?.onHoldList.length || 0);
			setnumberOfDelayedShipments(dashboardData?.delayedList.length || 0);
			setDelayedShipments(dashboardData?.delayedList || []);
		}
	}, [dashboardData]);

	return (
		<SectionWrapper ref={ref}>
			<S.SectionHeader>
				<Typography variant="p2" fontWeight="700">
					shipmentsNeedAction
				</Typography>
				<Tooltip placement="right" title={t('shipmentsNeedActionTooltip')}>
					<Icon icon={faCircleInfo} />
				</Tooltip>
				{!loading && (numberOfShipmentsOnHold > 0 || numberOfDelayedShipments > 0) && (
					<Badge
						variant="pill"
						size="small"
						color="red"
						text={
							numberOfShipmentsOnHold + numberOfDelayedShipments ||
							numberOfShipmentsOnHold ||
							numberOfDelayedShipments
						}
					/>
				)}
			</S.SectionHeader>

			{loading ? (
				<S.ContentWrapper>
					<Skeleton variant="text" width="100%" height="5rem" />
					<Skeleton variant="text" width="100%" height="5rem" />
				</S.ContentWrapper>
			) : (
				<>
					{numberOfShipmentsOnHold === 0 && numberOfDelayedShipments === 0 && (
						<S.ContentWrapper>
							<Image
								width="7.7rem"
								height="7.7rem"
								url="https://storage.googleapis.com/tryoto-public/public/assets/images/homepage/noShipmentNeedAction.svg"
							/>
							<Typography variant="p2" fontWeight="700">
								allShipmentsAreOnTrack
							</Typography>
							<Typography variant="p3" color="var(--content-2)">
								noShipmentsNeedActionDescription
							</Typography>
						</S.ContentWrapper>
					)}
					{numberOfShipmentsOnHold > 0 && (
						<Alert
							variant="warning"
							showIcon
							header={`${numberOfShipmentsOnHold} ${t(shipmentsNeedActionConfig[0].header)}`}
							content={shipmentsNeedActionConfig[0].description}
							ctaButtonLabel="viewDetails"
							onCtaButtonClick={() =>
								navigate(shipmentsNeedActionConfig[0].navigationLink, {
									state: {
										defaultFilters: []
									}
								})
							}
							className={numberOfDelayedShipments > 0 ? 'mb-4' : ''}
						/>
					)}
					{numberOfDelayedShipments > 0 && (
						<Alert
							variant="error"
							showIcon
							iconUrl={shipmentsNeedActionConfig[1].imageUrl}
							header={`${numberOfDelayedShipments} ${t(shipmentsNeedActionConfig[1].header)}`}
							content={shipmentsNeedActionConfig[1].description}
							ctaButtonLabel="viewDetails"
							onCtaButtonClick={() =>
								navigate(shipmentsNeedActionConfig[1].navigationLink, {
									state: {
										defaultFilters: [
											{
												column: {
													Header: 'ID',
													accessor: 'ID',
													filterType: FilterType.number
												},
												action: FilterAction.multipleFilter,
												...(body && {
													value: [...delayedShipments]
												})
											}
										]
									}
								})
							}
						/>
					)}
				</>
			)}
		</SectionWrapper>
	);
};

export default ShipmentsNeedAction;
